import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm_password: "", upi_id: "", terms: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (!form.terms) {
      setError("Please accept the Terms & Conditions");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (err) {
      setError(fmtErr(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Brand /></div>
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8" data-testid="register-card">
          <h1 className="text-2xl font-bold font-heading text-slate-900">Create your account</h1>
          <p className="mt-1.5 text-sm text-slate-600">Start testing apps and earning rewards today — it's free.</p>
          {error && <div data-testid="register-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full name</label>
              <input data-testid="register-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone number</label>
              <input data-testid="register-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="10-digit mobile number" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">UPI ID</label>
              <input data-testid="register-upi-input" required value={form.upi_id} onChange={set("upi_id")} className={inputCls} placeholder="yourname@upi" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} className={inputCls} placeholder="Min 6 characters" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
                <input data-testid="register-confirm-password-input" type="password" required value={form.confirm_password} onChange={set("confirm_password")} className={inputCls} placeholder="Repeat password" />
              </div>
            </div>
            <label className="flex items-start gap-2.5 text-sm text-slate-600">
              <input data-testid="register-terms-checkbox" type="checkbox" checked={form.terms} onChange={set("terms")} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span>I agree to the Terms & Conditions and confirm that all feedback I submit will be my genuine, honest opinion.</span>
            </label>
            <button type="submit" data-testid="register-submit-btn" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            Already have an account? <Link to="/login" data-testid="register-login-link" className="font-semibold text-blue-700 hover:text-blue-800">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
